import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';
import { store } from './store';
import { getConfig } from './config';
import { getPendingClientId } from './services/authService';
import LoginPage from './pages/Login';
import HomePage from './pages/Home';
import { HomepageView } from './pages/Home';
import ProjectPage from './pages/Project';
import { WorkspaceUserGroupDiagnosticsPage } from './pages/Diagnostics';
import Loading from './components/Loading';
import './styles/global.css';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading message="Loading..." fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Main app routes
function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading message="Initializing..." fullScreen />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to="/" replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <HomepageView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:projectId/*"
        element={
          <ProtectedRoute>
            <ProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diagnostics"
        element={
          <ProtectedRoute>
            <WorkspaceUserGroupDiagnosticsPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// AppProvider wrapper that gets application from AuthContext
function AppProviderWithAuth({ children }: { children: React.ReactNode }) {
  const { application, baseNamespaces } = useAuth();
  
  return (
    <AppProvider initialApplication={application} baseNamespaces={baseNamespaces}>
      {children}
    </AppProvider>
  );
}

// Determine the client ID to use
function getInitialClientId(): string | undefined {
  const config = getConfig();
  
  // Priority:
  // 1. Config value (if set)
  // 2. Stored client ID (from previous auth or pending OAuth)
  
  if (config.applicationId) {
    return config.applicationId;
  }
  
  // Check for stored client ID (either from pending OAuth or previous session)
  const storedClientId = getPendingClientId();
  if (storedClientId) {
    console.log('Using stored client ID:', storedClientId);
    return storedClientId;
  }
  
  return undefined;
}

// App wrapper with providers
function App() {
  const initialClientId = getInitialClientId();

  return (
    <Provider store={store}>
      <HashRouter>
        <AuthProvider initialClientId={initialClientId}>
          <AppProviderWithAuth>
            <AppRoutes />
          </AppProviderWithAuth>
        </AuthProvider>
      </HashRouter>
    </Provider>
  );
}

export default App;
